import {gql} from 'apollo-angular';
import {Injectable} from '@angular/core';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {Observable} from 'rxjs';
import {map, pluck} from 'rxjs/operators';
import {NetworkAccount} from '../interfaces/networks';
import {LinkedNetworksResolver} from '../resolvers/linked-networks.resolver';
import {listNetworksQuery} from '../resolvers/list-networks.query';
import {MediationEngine} from 'src/globalTypes';

export const networkAccountFragments = gql`
    fragment NetworkAccountCommonAttributes on NetworkAccount {
        id
        isDefault
        isUserAccount
        login
        networkId
        __typename
        permissions {
            canDelete
            canEdit
        }
    }
    fragment AmazonAccountAttributes on AmazonAccount {
        s3Location
        s3AccessKeyId
        s3SecretAccessKey
    }
    fragment NetworkAccountAttributes on NetworkAccount {
        ...NetworkAccountCommonAttributes
        ... on AdcolonyAccount {
            apiKey
        }
        ... on AdmobAccount {
            accountId
            clientId
            clientSecret
            refreshToken
            reportingStatus
            isCustomersSecrets
        }
        ... on AmazonAccount {
            ...AmazonAccountAttributes
        }
        ... on ApplovinAccount {
            apiKey
            sdkKey
        }
        ... on BigoAdsAccount {
            devId
            token
        }
        ... on ChartboostAccount {
            userId
            userSignature
        }
        ... on FacebookAccount {
            reportingStatus
        }
        ... on FlurryAccount {
            apiKey
        }
        ... on FyberAccount {
            consumerKey
            publisherId
            secretKey
        }
        ... on InmobiAccount {
            accountId
            apiKey
        }
        ... on IronSourceAccount {
            reportingSecretKey
        }
        ... on MobileFuseAccount {
            publisherId
        }
        ... on MobvistaAccount {
            apiKey
            sKey
            secretKey
        }
        ... on MopubAccount {
            apiKey
            reportKey
        }
        ... on OguryAccount {
            reportingApiToken
        }
        ... on PubnativeAccount {
            reportToken
        }
        ... on SmaatoAccount {
            clientKey
            clientSecret
            publisherId
        }
        ... on StartappAccount {
            devId
            partnerId
            token
        }
        ... on TapjoyAccount {
            publisherReportingApiKey
        }
        ... on TargetAccount {
            newAccessToken
        }
        ... on UnityAdsAccount {
            apiKey
            organizationCoreId
        }
        ... on VungleAccount {
            apiKey
        }
        ... on YandexAccount {
            oauthToken
        }
    }
`;

const makeDefaultMutation = gql`
    mutation makeDefaultNetworkAccount($userId: ID!, $networkId: ID!, $accountId: ID!) {
        setUserDefaultNetworkAccount(userId: $userId, networkId: $networkId, accountId: $accountId) {
            ...NetworkAccountAttributes
        }
    }
`;

const networkAccountsQuery = gql`
    query listUserNetworkAccounts($userId: ID!, $networkId: ID!, $monetizationEngine: MediationEngine) {
        listUserNetworkAccounts(userId: $userId, networkId: $networkId, monetizationEngine: $monetizationEngine) {
            ...NetworkAccountAttributes
        }
    }
`;

const deleteUserNetworkAccountMutation = gql`
    mutation deleteUserNetworkAccount($userId: ID!, $accountId: ID!) {
        deleteUserNetworkAccount(userId: $userId, accountId: $accountId)
    }
`;

const createNetworkAccountsMutation = gql`
    mutation createUserNetworkAccount($userId: ID!, $networkId: ID!, $attributes: NewUserNetworkAccountAttributes!) {
        createUserNetworkAccount(userId: $userId, networkId: $networkId, attributes: $attributes) {
            ...NetworkAccountAttributes
        }
    }
`;

const updateNetworkAccountsMutation = gql`
    mutation updateUserNetworkAccount($userId: ID!, $accountId: ID!, $attributes: ExistingUserNetworkAccountAttributes!) {
        updateUserNetworkAccount(userId: $userId, accountId: $accountId, attributes: $attributes) {
            ...NetworkAccountAttributes
        }
    }
`;

const enableAdmobAccountReportingMutation = gql`
    mutation enableAdmobAccountReporting($userId: ID!) {
        enableAdmobAccountReporting(userId: $userId) {
            oAuthUrl
        }
    }
`;

@Injectable()
export class NetworkAccountsService {
    private currentMediationEngine: MediationEngine = null;
    get canManageNetworkAccounts() {
        return this.networksResolver.permissions.canManageNetworkAccounts;
    }

    constructor(
        private api: ApiService,
        private networksResolver: LinkedNetworksResolver
    ) {
        this.api.registerFragments(networkAccountFragments);
    }

    fetchAccounts(networkId: string, mediationEngine: MediationEngine = null): Promise<Observable<NetworkAccount[]>> {
        this.currentMediationEngine = mediationEngine;
        const queryRef = this.api.watchQuery({
            query: networkAccountsQuery,
            fetchPolicy: 'cache-first',
            variables: {
                networkId,
                userId: this.networksResolver.getUserId(),
                ...(mediationEngine && {monetizationEngine: mediationEngine}),
            },
        });
        return queryRef.result().then(
            () => <Observable<NetworkAccount[]>>queryRef.valueChanges.pipe(
                    pluck('listUserNetworkAccounts'),
                    map((list: NetworkAccount[]) => [...list].sort((a, b) => (a.login > b.login ? 1 : -1)))
                )
        );
    }

    makeDefault(networkAccount: NetworkAccount) {
        return this.api.mutate({
            mutation: makeDefaultMutation,
            variables: {
                accountId: networkAccount.id,
                networkId: networkAccount.networkId,
                userId: this.networksResolver.getUserId(),
            },
            update: this.api.cacheHelper.updateQuery(
                {
                    query: listNetworksQuery,
                    variables: {
                        userId: this.networksResolver.getUserId(),
                        ...(this.currentMediationEngine && {monetizationEngine: this.currentMediationEngine}),
                    },
                },
                (cache: {listUserNetworkAccounts: any[]}, {setUserDefaultNetworkAccount}) => {
                    const updatedAccounts = setUserDefaultNetworkAccount || [];
                    cache.listUserNetworkAccounts = [...cache.listUserNetworkAccounts, ...updatedAccounts].filter(
                        (account: NetworkAccount) => account.isDefault
                    );
                    return cache;
                }
            ),
        });
    }

    deleteAccount(networkAccount: NetworkAccount) {
        return this.api.mutate({
            mutation: deleteUserNetworkAccountMutation,
            variables: {
                accountId: networkAccount.id,
                userId: this.networksResolver.getUserId(),
            },
            awaitRefetchQueries: true,
            refetchQueries: networkAccount.isDefault
                ? [
                      {
                          query: listNetworksQuery,
                          variables: {
                              userId: this.networksResolver.getUserId(),
                              ...(this.currentMediationEngine && {monetizationEngine: this.currentMediationEngine}),
                          },
                      },
                  ]
                : [],
            update: this.api.cacheHelper.removeEntity(networkAccount),
        });
    }

    createAccount(networkId: string, networkAccount: Partial<NetworkAccount>): Observable<any> {
        return this.api
            .mutate({
                mutation: createNetworkAccountsMutation,
                variables: {
                    userId: this.networksResolver.getUserId(),
                    networkId,
                    attributes: networkAccount,
                },
                awaitRefetchQueries: true,
                refetchQueries: ({data}: any) =>
                    data.createUserNetworkAccount.isDefault
                        ? [
                              {
                                  query: listNetworksQuery,
                                  variables: {
                                      userId: this.networksResolver.getUserId(),
                                      ...(this.currentMediationEngine && {monetizationEngine: this.currentMediationEngine}),
                                  },
                              },
                          ]
                        : [],
                update: this.api.cacheHelper.updateQuery(
                    {
                        query: networkAccountsQuery,
                        variables: {
                            networkId,
                            userId: this.networksResolver.getUserId(),
                            ...(this.currentMediationEngine && {monetizationEngine: this.currentMediationEngine}),
                        },
                    },
                    (cache: {listUserNetworkAccounts: any[]}, {createUserNetworkAccount}) => {
                        if (createUserNetworkAccount.isDefault) {
                            cache.listUserNetworkAccounts = cache.listUserNetworkAccounts.map(account =>
                                account.isDefault ? {...account, isDefault: false} : account
                            );
                        }
                        cache.listUserNetworkAccounts = [...cache.listUserNetworkAccounts, createUserNetworkAccount];
                        return cache;
                    }
                ),
            })
            .pipe(pluck('createUserNetworkAccount'));
    }

    updateAccount(account: NetworkAccount, changedAccountAttributes: Partial<NetworkAccount>) {
        return this.api
            .mutate({
                mutation: updateNetworkAccountsMutation,
                variables: {
                    userId: this.networksResolver.getUserId(),
                    accountId: account.id,
                    attributes: changedAccountAttributes,
                },
            })
            .pipe(pluck('updateUserNetworkAccount'));
    }

    enableAdmobAccountReporting(userId?: string) {
        return this.api
            .mutate({
                mutation: enableAdmobAccountReportingMutation,
                variables: {
                    userId: userId ? userId : this.networksResolver.getUserId(),
                },
            })
            .pipe(pluck('enableAdmobAccountReporting'));
    }
}
